import Config from "../../general/config";
import { markdownHtml, markdownText, MARKDOWN_STYLES } from '../../general/markdown';
import { addMarkdownFieldHelp, MARKDOWN_FIELD_HELP_STYLES, MARKDOWN_FIELD_HELP_SCRIPT } from './markdown-field-help';
import { FormFieldDto, ManagedFragmentDto } from "../../general/dtos";
import { FragmentAnswers, renderAnswerTables } from './fragment-answers';
import { isMultiChoiceType, selectionKeys, selectionsMatch, expectsBlankInline } from './multi-choice-fields';
import { fieldDisplayNames } from './field-display-names';
import { FieldLayout, readFieldLayout } from '../../general/field-container-layout';
import { parseGraph } from '../content-creator/graph-model';
import { createGraphViewport, GRAPH_VIEWPORT_SCRIPT } from './graph-viewport';
import { CARD_GROUP_STYLES } from '../../general/card-group-layout';
import { prepareResponsiveTables, TABLE_RESPONSIVE_STYLES } from '../../general/table-responsive';
import { COLUMNS_STYLES } from '../../general/columns-layout';
import { LESSON_CONTENT_STYLES } from '../../general/lesson-content-styles';
import { prepareYouTubeVideos } from '../../general/youtube-video';

export const RENDERER_SCOPED_STYLES: string = `
${MARKDOWN_STYLES}
${MARKDOWN_FIELD_HELP_STYLES}
${CARD_GROUP_STYLES}
${TABLE_RESPONSIVE_STYLES}
${COLUMNS_STYLES}
${LESSON_CONTENT_STYLES}
.graph-viewer { width:100%; max-width:100%; }
.graph-viewport { height:clamp(14rem,45vh,28rem); overflow:hidden; border:1px solid var(--surface-border,#aaa); border-radius:.5rem; cursor:grab; touch-action:none; user-select:none; }
.graph-viewport.is-panning, .graph-viewport.is-panning .graph-node { cursor:grabbing !important; }
.graph-viewport:focus-visible { outline:2px solid var(--primary-color); outline-offset:2px; }
.graph-viewport-footer { display:flex; flex-wrap:wrap; align-items:center; justify-content:space-between; gap:.5rem; padding:.4rem 0; color:var(--text-color-secondary,inherit); }
.graph-zoom-controls { display:flex; flex-wrap:wrap; gap:.25rem; align-items:center; }
.graph-zoom-controls button { font:inherit; font-size:.875rem; line-height:1.25rem; min-width:2rem; padding:.2rem .5rem; border:1px solid var(--surface-border,#aaa); border-radius:.25rem; background:var(--surface-card,#fff); color:var(--text-color,#333); cursor:pointer; }
.graph-zoom-controls button:hover:not(:disabled) { border-color:var(--primary-color); color:var(--primary-color); }
.graph-zoom-controls button:disabled { opacity:.45; cursor:default; }
.graph-zoom-controls button:focus-visible { outline:2px solid var(--primary-color); outline-offset:2px; }
.graph-zoom-level { min-width:3rem; text-align:center; font-size:.875rem; font-variant-numeric:tabular-nums; }
div[data-type="graph-component"] .graph-node { cursor: pointer; outline: none; }
div[data-type="graph-component"] .node-rect { transition: stroke-width .2s; }
div[data-type="graph-component"] .graph-node:hover .node-rect,
div[data-type="graph-component"] .graph-node:focus .node-rect { stroke-width: 3; }
.field-layout { display:grid; grid-template-columns:var(--field-label-column) minmax(0,1fr); column-gap:var(--field-column-gap); row-gap:var(--field-row-gap); max-width:100%; }
.field-layout > .form-field-container { display:grid !important; grid-template-columns:subgrid; grid-column:1 / -1; gap:inherit !important; min-width:0; }
.field-layout > .form-field-container > label { white-space:normal; overflow-wrap:anywhere; min-width:0; text-align:var(--field-label-align); }
.field-layout > .form-field-container > [data-field-key] { min-width:0; box-sizing:border-box; }
.field-layout > .form-field-container.is-label-hidden > [data-field-key] { grid-column:2; }
.field-layout [data-field-key] label { white-space:normal; }
@media(max-width:640px) {
  .field-layout[data-stack-on-mobile="true"] { grid-template-columns:minmax(0,1fr); }
  .field-layout[data-stack-on-mobile="true"] > .form-field-container { grid-template-columns:minmax(0,1fr); }
  .field-layout[data-stack-on-mobile="true"] > .form-field-container > label { text-align:left; }
  .field-layout[data-stack-on-mobile="true"] > .form-field-container.is-label-hidden > [data-field-key] { grid-column:1; }
}
.inline-text-field-container { display: inline; }
.inline-text-field-container input.inline-text-input, .field-layout input.inline-text-input {
    display: inline-block; box-sizing: border-box; width: 10ch; max-width: 100%;
    font: inherit; line-height: inherit; color: inherit; vertical-align: baseline;
    margin: 0 0.1em; padding: 0 0.15em; border: 0; border-bottom: 1px solid currentColor;
    border-radius: 0; box-shadow: none; background-color: transparent;
    background-color: color-mix(in srgb, currentColor 4%, transparent);
}
.inline-text-field-container input.inline-text-input:focus-visible {
    outline: 2px solid var(--primary-color, currentColor); outline-offset: 2px;
}
.inline-text-field-container input.inline-text-input.invalid-field { box-shadow: none !important; }
.multi-choice-group { display: flex; flex-direction: column; gap: 0.5rem; margin: 0; padding: 0.5rem; border: 0; }
.form-field-container .multi-choice-group label { white-space: normal; display: flex; gap: 0.5rem; align-items: flex-start; min-width: 0; }
.multi-choice-dropdown { position: relative; min-width: 12rem; }
.multi-choice-dropdown summary { cursor: pointer; padding: 0.5rem; border: 1px solid currentColor; border-radius: 0.25rem; white-space: normal; overflow-wrap: anywhere; }
.multi-choice-dropdown .multi-choice-options { position: absolute; left: 0; top: 100%; width: 100%; box-sizing: border-box; z-index: 10; max-height: 16rem; overflow: auto; background: var(--surface-overlay, white); color: var(--text-color, black); border: 1px solid currentColor; padding: 0.5rem; box-shadow: 0 2px 6px #0003; }
.form-field-container .multi-choice-options label { display: flex; gap: 0.5rem; align-items: flex-start; white-space: normal; min-width: 0; padding: 0.25rem; }
.multi-choice-option-text { flex: 1; min-width: 0; white-space: normal; overflow-wrap: anywhere; }
.multi-choice-group input[type="checkbox"], .multi-choice-options input[type="checkbox"] { flex-shrink: 0; }
.fragment-answers-table { border-collapse: collapse; width: 100%; }
.fragment-answers-table .inline-blank-highlight { color: var(--primary-color, #2563eb); font-weight: 700; }
.fragment-answers-table th, .fragment-answers-table td { border: 1px solid currentColor; padding: 0.5rem; text-align: left; vertical-align: top; }
.form-field-container {
    display: flex !important;
    flex-wrap: wrap;
    gap: 0.6rem !important;
    min-width: 0;
    max-width: 100%;
}

.form-field-container label {
    white-space: normal;
    overflow-wrap: anywhere;
    min-width: 0;
    max-width: 100%;
}

.form-field-container > [data-field-key] {
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
}

/* Explicit aria-labelledby references keep hidden labels accessible without a layout box. */
.form-field-container > label.field-label-hidden {
    display: none !important;
}

.invalid-field {
    border-color: var(--red-500) !important;
    box-shadow: 0 0 0 1px var(--red-500) !important;
}

/* Custom-styled select with arrow */
select.p-dropdown {
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    appearance: none !important;

    height: 2.5rem !important;
    padding: 0 2.2rem 0 0.75rem !important; /* room for arrow */
    line-height: 2.5rem !important;
    border-radius: var(--border-radius) !important;
    font-size: 1rem !important;
    box-sizing: border-box !important;

    /* Custom arrow */
    background-image: url("data:image/svg+xml;utf8,<svg fill='gray' height='16' width='16' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/></svg>");
    background-repeat: no-repeat;
    background-position: right 0.6rem center;
    background-size: 1rem;
}

/* Make textarea visually consistent with input */
textarea.p-inputtextarea {
    min-height: 2.5rem !important;
    padding: 0.5rem 0.75rem !important;
    box-sizing: border-box !important;
    resize: both;
}

/* Table layout per field */
table.form-field-table {
    border-collapse: separate;
    border-spacing: 0.6rem 0; /* spacing between label and input */
}

table.form-field-table td {
    vertical-align: middle;
    padding: 0;
    border: none;
}

table.form-field-table td:first-child {
    white-space: normal;
    overflow-wrap: anywhere;
}
`;

export function renderFragmentHtml(
  html: string,
  fields: FormFieldDto[],
  answerContext?: { fragments: ManagedFragmentDto[]; currentKey: string; answers: FragmentAnswers }
): string {

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  renderAnswerTables(doc, answerContext?.fragments ?? [], answerContext?.currentKey ?? null, answerContext?.answers ?? {});
  prepareResponsiveTables(doc);
  prepareYouTubeVideos(doc);

  // Styles must be the first node in the fragment
  const style = doc.createElement("style");
  style.textContent = RENDERER_SCOPED_STYLES;

  const root = doc.body;
  root.querySelectorAll<HTMLElement>('div[data-type="graph-component"]').forEach(container => {
    try {
      const graph = parseGraph({ nodesJson: container.getAttribute('data-nodes') || '[]',
        edgesJson: container.getAttribute('data-edges') || '[]', examplesJson: container.getAttribute('data-examples') || '{}' });
      container.replaceChildren(createGraphViewport(doc, graph, container.getAttribute('data-height')));
      container.style.width = '100%';
      container.style.height = 'auto';
    } catch (error) {
      const message = doc.createElement('p');
      message.setAttribute('role', 'alert');
      message.textContent = 'This graph could not be displayed: ' + (error as Error).message;
      container.replaceChildren(message);
    }
  });
  root.querySelectorAll('figure figcaption').forEach(caption => {
    if (!caption.textContent?.trim()) caption.remove();
  });
  root.insertBefore(style, root.firstChild);
  root.querySelectorAll('field-container').forEach(element => {
    const layout = readFieldLayout(element);
    const container = doc.createElement('div');
    container.className = 'field-layout';
    container.setAttribute('data-stack-on-mobile', String(layout.stackOnMobile));
    // Retain the serialized settings for each field's rendering defaults.
    for (const attribute of Array.from(element.attributes)) if (attribute.name.startsWith('data-')) container.setAttribute(attribute.name, attribute.value);
    container.setAttribute('data-stack-on-mobile', String(layout.stackOnMobile));
    container.style.width = layout.width;
    container.style.setProperty('--field-label-column', layout.labelWidth === 'auto' ? 'fit-content(35%)' : layout.labelWidth);
    container.style.setProperty('--field-column-gap', layout.columnGap);
    container.style.setProperty('--field-row-gap', layout.rowGap);
    container.style.setProperty('--field-label-align', layout.labelAlign);
    for (const child of Array.from(element.children)) {
      if (child.tagName.toLowerCase() === 'form-field' && fields?.some(field => field.key === child.getAttribute('key') && field.type !== 'quiz-list')) container.appendChild(child);
    }
    element.replaceWith(container);
  });
  const resetActions: string[] = [];

  // Build fields
  const inlineBlankNumbers = new Map(Array.from(root.querySelectorAll('form-field'))
    .filter(node => fields?.some(field => field.key === node.getAttribute('key') && field.type === 'inline-text'))
    .map((node, index) => [node.getAttribute('key'), index + 1]));
  fields?.forEach(field => {
    const placeholder = doc.querySelector(`form-field[key="${field.key}"]`);
    if (!placeholder) return;

    const layoutContainer = placeholder.closest('.field-layout');
    const layout = layoutContainer ? readFieldLayout(layoutContainer) : undefined;
    const fieldContainer: HTMLElement = createFormFieldDom(field, doc, inlineBlankNumbers.get(field.key), layout);
    const paragraph = fieldContainer.tagName === 'DIV' ? placeholder.closest('p') : null;
    if (paragraph) {
      // A div inside p is invalid: reparsing would insert empty paragraphs around it.
      // Split the paragraph to keep surrounding text and inline formatting intact.
      const before = doc.createRange();
      before.selectNodeContents(paragraph);
      before.setEndBefore(placeholder);
      const after = doc.createRange();
      after.selectNodeContents(paragraph);
      after.setStartAfter(placeholder);
      const parts: HTMLElement[] = [];
      const appendParagraph = (content: DocumentFragment) => {
        if (!content.textContent?.trim() && !content.querySelector('br, img, video, audio, iframe, svg, canvas, input, select, textarea, button, form-field, quiz-target')) return;
        const part = paragraph.cloneNode(false) as HTMLElement;
        part.appendChild(content);
        parts.push(part);
      };
      appendParagraph(before.cloneContents());
      parts.push(fieldContainer);
      appendParagraph(after.cloneContents());
      paragraph.replaceWith(...parts);
    } else {
      placeholder.replaceWith(fieldContainer);
    }

    // reset form generation
    const key = field.key;
    const def = field.defaultValue;
    const hasDefault = def !== null && def !== undefined;

    switch (field.type) {
        case 'checkbox-group':
        case 'multi-select':
            const selected = JSON.stringify(selectionKeys(def)).replace(/</g, '\\u003c');
            resetActions.push(`
                el = document.getElementById(${JSON.stringify(key).replace(/</g, '\\u003c')});
                if (el) el.querySelectorAll('input[type=checkbox]').forEach(option => {
                    option.checked = ${selected}.includes(option.value);
                });
            `);
            break;
        case "checkbox":
            const checked = (hasDefault && def === true) ? "true" : "false";
            resetActions.push(`
                el = document.getElementById("${key}");
                if (el) el.checked = ${checked};
            `);
            break;

        case "radio":
            const radioVal = hasDefault ? String(def) : "";
            resetActions.push(`
                document.querySelectorAll("input[name='${key}']").forEach(r => {
                r.checked = (r.value === ${JSON.stringify(radioVal)} && ${hasDefault});
                });
            `);
            break;

        case "select":
        case "textarea":
        case "text":
        default:
            const val = JSON.stringify(hasDefault ? String(def) : "");
            resetActions.push(`
                el = document.getElementById("${key}");
                if (el) el.value = ${val};
            `);
            break;
    }

  });

  // QUIZ
  const quizPlaceholders = doc.querySelectorAll('quiz-target');
  quizPlaceholders.forEach(placeholder => {
    const fieldKey = placeholder.getAttribute('field-key');
    const expectedKey = placeholder.getAttribute('expected-key');
    const field = fields.find(f => f.key === fieldKey);

    if (field && field.type === 'quiz-list') {
      // Create just the dropdown (no label/container) for inline placement
      const dropdown = createQuizDropdownDom(field, expectedKey, doc);
      placeholder.replaceWith(dropdown);

      // Add to reset actions (default to empty/placeholder)
      resetActions.push(`
        el = document.querySelector('select[data-expected-key="${expectedKey}"][data-field-key="${fieldKey}"]');
        if (el) el.value = "";
      `);
    }
  });

  // Validation + value extraction + context menu suppression
  const script = doc.createElement("script");
  script.textContent = String.raw`
    function fitMultiChoiceDropdown(group) {
      if (!group.open) return;
      const panel = group.querySelector('.multi-choice-options');
      if (!panel) return;
      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      const margin = 8;
      // Let the browser measure the options using their actual font and checkbox spacing.
      panel.style.width = 'max-content';
      panel.style.left = '0px';
      const natural = panel.getBoundingClientRect();
      const width = Math.min(Math.max(group.getBoundingClientRect().width, Math.ceil(natural.width)),
        Math.max(1, viewportWidth - margin * 2));
      const left = Math.max(margin, Math.min(natural.left, viewportWidth - margin - width));
      panel.style.width = width + 'px';
      panel.style.left = (left - natural.left) + 'px';
    }
    function fitOpenMultiChoiceDropdowns() {
      document.querySelectorAll('details[data-multi-choice][open]').forEach(fitMultiChoiceDropdown);
    }
    document.addEventListener('toggle', event => {
      if (event.target.matches('details[data-multi-choice]')) fitMultiChoiceDropdown(event.target);
    }, true);
    window.addEventListener('resize', fitOpenMultiChoiceDropdowns);
    if (document.fonts) document.fonts.ready.then(fitOpenMultiChoiceDropdowns);
    window.syncMultiChoices = function() {
      document.querySelectorAll('[data-multi-choice]').forEach(group => {
        const summary = group.querySelector('summary');
        if (!summary) return;
        const labels = Array.from(group.querySelectorAll('input[type=checkbox]:checked'))
          .map(input => input.parentElement.textContent.trim());
        summary.textContent = labels.join(', ') || group.getAttribute('data-placeholder') || 'Select options...';
        if (group.matches('details')) fitMultiChoiceDropdown(group);
      });
    };
    document.addEventListener('change', event => {
      if (event.target.closest('[data-multi-choice]')) window.syncMultiChoices();
    });
    document.addEventListener('click', event => {
      document.querySelectorAll('details[data-multi-choice][open]').forEach(group => {
        if (!group.contains(event.target)) group.open = false;
      });
    });
    document.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('details[data-multi-choice][open]').forEach(group => {
        group.open = false;
        group.querySelector('summary').focus();
      });
    });
    window.syncMultiChoices();
    window.validateForm = function() {
      const fields = document.querySelectorAll("[data-field-key]");
      const errors = {};

      fields.forEach(element => {
        const el = element;
        const key = el.getAttribute("data-field-key");
        if (!key) return;

        const fieldErrors = [];
        if (el.hasAttribute('data-multi-choice')) {
          const missing = el.hasAttribute('required') && !el.querySelector('input[type=checkbox]:checked');
          el.classList.toggle('invalid-field', Boolean(missing));
          if (missing) errors[key] = ['Select at least one option.'];
          return;
        }

        // RADIO GROUP HANDLING (individual radio inputs)
        if (el instanceof HTMLInputElement && el.type === "radio") {
          const group = document.querySelectorAll(
            "input[type=radio][name='" + el.name + "']"
          );

          const anyChecked = Array.from(group).some(r => r.checked);

          if (!anyChecked) {
            fieldErrors.push("A value must be selected.");
            group.forEach(r => r.classList.add("invalid-field"));
          } else {
            group.forEach(r => r.classList.remove("invalid-field"));
          }

          if (fieldErrors.length > 0) errors[key] = fieldErrors;
          return;
        }

        // SKIP NON-FORM ELEMENTS (e.g., <span> wrapping radio group)
        if (!(el instanceof HTMLInputElement ||
              el instanceof HTMLSelectElement ||
              el instanceof HTMLTextAreaElement)) {
          return;
        }

        // NORMAL VALIDATION
        if (!el.checkValidity()) {
          if (el.validity.valueMissing) fieldErrors.push("This field is required.");
          if (el.validity.typeMismatch) fieldErrors.push("Invalid format.");
          if (el.validity.patternMismatch) fieldErrors.push("Invalid value.");
          if (el.validity.rangeOverflow) fieldErrors.push("Value is too large.");
          if (el.validity.rangeUnderflow) fieldErrors.push("Value is too small.");
          if (el.validity.tooLong) fieldErrors.push("Value is too long.");
          if (el.validity.stepMismatch) fieldErrors.push("Invalid step value.");

          // STRICT EMAIL VALIDATION
          if (el.type === "email") {
            const strictEmailPattern = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/;
            if (!strictEmailPattern.test(el.value)) {
              fieldErrors.push("Invalid email address.");
            }
          }

          el.classList.add("invalid-field");
        } else {
          el.classList.remove("invalid-field");
        }

        if (fieldErrors.length > 0) errors[key] = fieldErrors;
      });

      return errors;
    };

    window.getFormValues = function() {
        const fields = document.querySelectorAll("[data-field-key]");
        const data = {};

        fields.forEach(el => {
            const key = el.getAttribute("data-field-key");
            const expected = el.getAttribute("data-expected-key");
            if (!key) return;
            if (el.hasAttribute('data-multi-choice')) {
                data[key] = Array.from(el.querySelectorAll('input[type=checkbox]:checked')).map(input => input.value);
                return;
            }
            if (!(el instanceof HTMLInputElement) && el.querySelector('input[type=radio]')) {
                data[key] = el.querySelector('input[type=radio]:checked')?.value ?? '';
                return;
            }

            let value = "";
            if (el instanceof HTMLInputElement) {
                if (el.type === "checkbox") value = el.checked;
                else if (el.type === "radio") {
                    if (el.checked) value = el.value;
                    else return; 
                } else value = el.value;
            } else {
                value = el.value;
            }

            const storageKey = expected ? (key + "::" + expected) : key;
            data[storageKey] = value;
        });

        return data;
    };
    document.addEventListener('contextmenu', e => e.preventDefault());

    window.resetForm = function() {
      ${resetActions.join("\n      ")}
      document.querySelectorAll(".invalid-field").forEach(el => el.classList.remove("invalid-field"));
      window.syncQuizPools();
      window.syncMultiChoices();
    };

    // Add this into the script block within your renderFragmentHtml function
    window.syncQuizPools = function() {
        const allQuizSelects = document.querySelectorAll('select.quiz-target-input');
        
        // Group selects by their field-key
        const groups = {};
        allQuizSelects.forEach(select => {
            const key = select.getAttribute('data-field-key');
            if (!groups[key]) groups[key] = [];
            groups[key].push(select);
        });

        // For each group, find which keys are currently selected
        Object.values(groups).forEach(group => {
            const selectedKeys = group
                .map(s => s.value)
                .filter(val => val !== "");

            // Update every dropdown in the group
            group.forEach(select => {
                const currentValue = select.value;
                const options = select.querySelectorAll('option:not([hidden])');

                options.forEach(opt => {
                    // An option should be hidden if it's selected elsewhere, 
                    // UNLESS it is the current selection of this specific dropdown
                    if (selectedKeys.includes(opt.value) && opt.value !== currentValue) {
                        opt.disabled = true;
                        opt.style.display = 'none';
                    } else {
                        opt.disabled = false;
                        opt.style.display = 'block';
                    }
                });
            });
        });
    };

${GRAPH_VIEWPORT_SCRIPT}

    window.postGraphClick = function(nodeId, element) {
        const container = element.closest('[data-type="graph-component"]');
        if (!container) return;
        const examplesStr = container.getAttribute("data-examples") || "{}";
        try {
            const examples = JSON.parse(examplesStr);
            const payload = examples[nodeId];
            if (payload) {
                window.parent.postMessage({ 
                    type: "graph-node-clicked", 
                    payload: Object.assign({ id: nodeId }, payload)
                }, "*");
            }
        } catch(e) { console.error("Click Parse Error", e); }
    };

    // Initialize listeners
    document.addEventListener('change', (e) => {
        if (e.target && e.target.classList.contains('quiz-target-input')) {
            window.syncQuizPools();
        }
    });

    setTimeout(() => {
        window.syncQuizPools();
        window.hydrateGraphs();
    }, 10);

    // selection notifier
    window.__aiSelectionButton = null;
    window.__aiSelectionContext = null;

    window.getNearestBlockElement = function(node) {
        let el = node && node.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
        while (el) {
            const tag = (el.tagName || "").toLowerCase();
            if (["p", "div", "li", "td", "th", "section", "article"].includes(tag)) {
                return el;
            }
            el = el.parentElement;
        }
        return null;
    };

    window.getDomPath = function(el) {
        if (!el || !el.parentElement) return "";
        const parts = [];
        let current = el;

        while (current && current.nodeType === Node.ELEMENT_NODE && current !== document.body) {
            const tag = current.tagName.toLowerCase();
            const siblings = Array.from(current.parentElement?.children || []).filter(
                s => s.tagName === current.tagName
            );
            const index = siblings.indexOf(current) + 1;
            parts.unshift(tag + ":nth-of-type(" + index + ")");
            current = current.parentElement;
        }

        return parts.join(" > ");
    };

    window.getContainingSentence = function(range) {
        const selected = (range?.toString?.() || "").trim();
        if (!selected) return "";

        const block = window.getNearestBlockElement(range.commonAncestorContainer);
        const text = (block?.textContent || "").trim();
        if (!text) return "";

        const idx = text.indexOf(selected);
        if (idx === -1) return text;

        const before = text.slice(0, idx);
        const after = text.slice(idx + selected.length);

        const sentenceStart = Math.max(
            before.lastIndexOf("."),
            before.lastIndexOf("!"),
            before.lastIndexOf("?")
        );

        const sentenceEndCandidates = [
            after.indexOf("."),
            after.indexOf("!"),
            after.indexOf("?")
        ].filter(i => i >= 0);

        const sentenceEnd = sentenceEndCandidates.length > 0
            ? Math.min(...sentenceEndCandidates)
            : after.length;

        return text.slice(sentenceStart + 1, idx + selected.length + sentenceEnd + 1).trim();
    };

    window.getTableContext = function(el) {
        const cell = el?.closest?.("td, th");
        if (!cell) return null;

        const row = cell.closest("tr");
        if (!row) return null;

        const table = row.closest("table");
        if (!table) return null;

        const rowCells = Array.from(row.children);
        const colIndex = rowCells.indexOf(cell);

        let rowHeader = "";
        const firstHeaderCell = row.querySelector("th");
        if (firstHeaderCell && firstHeaderCell !== cell) {
            rowHeader = (firstHeaderCell.textContent || "").trim();
        }

        let columnHeader = "";
        const allRows = Array.from(table.querySelectorAll("tr"));
        const headerRow = allRows[0];
        if (headerRow && colIndex >= 0) {
            const headerCells = Array.from(headerRow.children);
            const headerCell = headerCells[colIndex];
            if (headerCell) {
                columnHeader = (headerCell.textContent || "").trim();
            }
        }

        return {
            cellText: (cell.textContent || "").trim(),
            rowHeader: rowHeader,
            columnHeader: columnHeader
        };
    };

    window.buildAiSelectionContext = function() {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
            return null;
        }

        const range = selection.getRangeAt(0);
        const selectedText = selection.toString().trim();
        if (!selectedText) return null;

        const block = window.getNearestBlockElement(range.commonAncestorContainer);
        const tableContext = block ? window.getTableContext(block) : null;

        return {
            selectedText: selectedText,
            containingSentence: window.getContainingSentence(range),
            containingBlockText: block ? (block.textContent || "").trim() : "",
            containingBlockHtml: block ? block.outerHTML : "",
            nearestElementTag: block ? block.tagName.toLowerCase() : "",
            domPath: block ? window.getDomPath(block) : "",
            tableContext: tableContext
        };
    };

    window.hideAiSelectionButton = function() {
        if (window.__aiSelectionButton) {
            window.__aiSelectionButton.style.display = "none";
        }
    };

    window.ensureAiSelectionButton = function() {
        if (window.__aiSelectionButton) return window.__aiSelectionButton;

        const btn = document.createElement("button");
        btn.type = "button";
        btn.textContent = "✨ Explain";
        btn.style.position = "fixed";
        btn.style.zIndex = "999999";
        btn.style.display = "none";
        btn.style.padding = "6px 10px";
        btn.style.borderRadius = "999px";
        btn.style.border = "1px solid rgba(99,102,241,0.25)";
        btn.style.background = "linear-gradient(180deg, #ffffdf, #328f95)";
        btn.style.color = "#4338ca";
        btn.style.fontSize = "12px";
        btn.style.fontWeight = "600";
        btn.style.boxShadow = "0 6px 18px rgba(0,0,0,0.12)";
        btn.style.cursor = "pointer";

        btn.addEventListener("mousedown", function(e) {
            e.preventDefault();
            e.stopPropagation();
        });

        btn.addEventListener("click", function(e) {
            e.preventDefault();
            e.stopPropagation();

            if (window.__aiSelectionContext) {
                window.parent.postMessage({
                    type: "ai-selection-explain",
                    payload: window.__aiSelectionContext
                }, "*");
            }

            window.hideAiSelectionButton();
        });

        document.body.appendChild(btn);
        window.__aiSelectionButton = btn;
        return btn;
    };

    window.showAiSelectionButton = function() {
        if (!${Config.showAiControls}) return;
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
            window.hideAiSelectionButton();
            return;
        }

        const context = window.buildAiSelectionContext();
        if (!context || !context.selectedText) {
            window.hideAiSelectionButton();
            return;
        }

        window.__aiSelectionContext = context;

        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const btn = window.ensureAiSelectionButton();

        btn.style.display = "block";
        btn.style.left = Math.max(8, rect.left + window.scrollX) + "px";
        btn.style.top = Math.max(8, rect.bottom + window.scrollY + 8) + "px";
    };

    document.addEventListener("mouseup", function() {
        setTimeout(function() {
            window.showAiSelectionButton();
        }, 10);
    });

    document.addEventListener("keyup", function() {
        setTimeout(function() {
            window.showAiSelectionButton();
        }, 10);
    });

    document.addEventListener("scroll", function() {
        window.hideAiSelectionButton();
    }, true);

    document.addEventListener("mousedown", function(e) {
        if (window.__aiSelectionButton && !window.__aiSelectionButton.contains(e.target)) {
            setTimeout(function() {
                const selection = window.getSelection();
                if (!selection || selection.isCollapsed) {
                    window.hideAiSelectionButton();
                }
            }, 10);
        }
    });
  `;

  root.appendChild(script);
  const helpScript = doc.createElement('script');
  helpScript.textContent = MARKDOWN_FIELD_HELP_SCRIPT;
  root.appendChild(helpScript);

  return root.innerHTML;
}

export function setIframeAnswerFieldsReadOnly(iframe: HTMLIFrameElement | null, readOnly: boolean): void {
  const doc = iframe?.contentDocument;
  if (!doc) return;
  doc.querySelectorAll('[data-field-key], input:not([type="button"]):not([type="submit"]), button[type="reset"], select, textarea').forEach(field => {
    const controls = field.matches('input, button, select, textarea') ? [field] : Array.from(field.querySelectorAll('input, select, textarea'));
    controls.forEach(element => {
      const control = element as HTMLInputElement | HTMLButtonElement | HTMLSelectElement | HTMLTextAreaElement;
      if (readOnly) {
        if (!control.hasAttribute('data-answer-disabled-before-lock')) {
          control.setAttribute('data-answer-disabled-before-lock', String(control.disabled));
        }
        control.disabled = true;
      } else if (control.hasAttribute('data-answer-disabled-before-lock')) {
        control.disabled = control.getAttribute('data-answer-disabled-before-lock') === 'true';
        control.removeAttribute('data-answer-disabled-before-lock');
      }
    });
  });
}

export function restoreIframeValues(
  iframe: HTMLIFrameElement | null,
  values: Record<string, any>
): void {
  if (!iframe || !values) return;

  const doc = iframe.contentDocument;
  if (!doc) return;

  Object.keys(values).forEach(storageKey => {
    const val = values[storageKey];

    // 1. Handle Quiz Composite Keys (fieldKey::expectedKey)
    if (storageKey.includes("::")) {
      const [fieldKey, expectedKey] = storageKey.split("::");
      // Target the specific dropdown using both attributes
      const selector = `select[data-field-key="${fieldKey}"][data-expected-key="${expectedKey}"]`;
      const el = doc.querySelector(selector) as HTMLSelectElement;
      
      if (el) {
        el.value = String(val);
      }
      return; // Skip to next key
    }

    // 2. Handle Standard Fields (Original Logic)
    const elements = doc.querySelectorAll(`[data-field-key="${storageKey}"]`);

    elements.forEach(el => {
      if (el.hasAttribute('data-multi-choice')) {
        const selected = selectionKeys(val);
        el.querySelectorAll<HTMLInputElement>('input[type=checkbox]').forEach(option => {
          option.checked = selected.includes(option.value);
        });
        return;
      }
      const tag = el.tagName.toLowerCase();

      if (tag === "input") {
        const input = el as HTMLInputElement;
        if (input.type === "radio") {
          input.checked = input.value === String(val);
        } else if (input.type === "checkbox") {
          input.checked = Boolean(val);
        } else {
          input.value = String(val);
        }
      } else if (tag === "select" || tag === "textarea") {
        (el as any).value = String(val);
      }
    });
  });

  // 3. CRITICAL: Trigger the visual sync so "taken" options are hidden
  const win = iframe.contentWindow as any;
  win?.syncMultiChoices?.();
  if (win && typeof win.syncQuizPools === 'function') {
    win.syncQuizPools();
  }
}

export function resetIframeValues(
  iframe: HTMLIFrameElement | null,
  fields: FormFieldDto[]
): void {
  if (!iframe || !fields) return;

  const doc = iframe.contentDocument;
  if (!doc) return;

  fields.forEach(field => {
    const key = field.key;
    const def = field.defaultValue;
    const hasDefault = def !== null && def !== undefined;

    // Find elements by your custom data attribute
    const elements = doc.querySelectorAll(`[data-field-key="${key}"]`);

    elements.forEach(el => {
      const tag = el.tagName.toLowerCase();

      // Clear any validation styling during reset
      el.classList.remove("invalid-field");
      if (el.hasAttribute('data-multi-choice')) {
        const selected = selectionKeys(def);
        el.querySelectorAll<HTMLInputElement>('input[type=checkbox]').forEach(option => {
          option.checked = selected.includes(option.value);
        });
        return;
      }

      // 1. INPUT (Text, Radio, Checkbox, etc.)
      if (tag === "input") {
        const input = el as HTMLInputElement;

        if (input.type === "radio") {
          // If no default is defined, all radios in group remain unchecked
          input.checked = hasDefault && input.value === String(def);
          return;
        }

        if (input.type === "checkbox") {
          // Default to false if undefined/null
          input.checked = hasDefault ? Boolean(def) : false;
          return;
        }

        // Standard text/number/email inputs
        input.value = hasDefault ? String(def) : "";
        return;
      }

      // 2. SELECT
      if (tag === "select") {
        const select = el as HTMLSelectElement;
        select.value = hasDefault ? String(def) : (select.options[0]?.value || "");
        return;
      }

      // 3. TEXTAREA
      if (tag === "textarea") {
        const textarea = el as HTMLTextAreaElement;
        textarea.value = hasDefault ? String(def) : "";
        return;
      }
    });
  });
  (iframe.contentWindow as any)?.syncMultiChoices?.();
}

export function buildErrorMessage(errors: Record<string, string[]> | null | undefined, fields: FormFieldDto[], fragmentHtml = ''): string | null {
    if (!errors || Object.keys(errors).length === 0) {
        return null;
    }

    // Map field keys → labels
    const fieldLabels = Object.fromEntries(Object.entries(fieldDisplayNames(fields, fragmentHtml)).map(([key, name]) => [key, name.text]));

    const list = document.createElement('ul');
    for (const key of Object.keys(errors)) {
        const label = fieldLabels[key] ?? key;
        const messages = errors[key];
        const item = document.createElement('li');
        item.appendChild(document.createTextNode(label));
        const details = document.createElement('ul');
        for (const msg of messages) {
            const detail = document.createElement('li');
            detail.textContent = msg;
            details.appendChild(detail);
        }
        item.appendChild(details);
        list.appendChild(item);
    }
    return list.outerHTML;
}

export interface UserAnswerVerificationResult {
  field: FormFieldDto;
  userAnswer: string | string[];
  expectedAnswer: string | string[] | null;
  isCorrect: boolean | null;
  quizLabel?: string;
}

function normalize(value: any): string {
  if (value == null) return "";

  return String(value)
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

export function verifyUserAnswers(
  fields: FormFieldDto[],
  values: Record<string, any>
): UserAnswerVerificationResult[] {
  const results: UserAnswerVerificationResult[] = [];

  // Iterate through all stored values (both flat keys and composite keys)
  Object.keys(values).forEach(storageKey => {
    const userValue = values[storageKey];
    
    // 1. Handle Quiz Targets (Composite Keys: fieldKey::expectedKey)
    if (storageKey.includes("::")) {
      const [fieldKey, expectedKey] = storageKey.split("::");
      const field = fields.find(f => f.key === fieldKey);
      
      if (field) {
        results.push({
          field,
          userAnswer: String(userValue ?? ""),
          expectedAnswer: expectedKey, // The second half of our key IS the correct answer
          isCorrect: String(userValue) === expectedKey
        });
      }
      return;
    }

    // 2. Handle Standard Fields
    const field = fields.find(f => f.key === storageKey);
    if (field && isMultiChoiceType(field.type)) {
      results.push({ field, userAnswer: selectionKeys(userValue),
        expectedAnswer: Array.isArray(field.expectedValue) ? field.expectedValue : null,
        isCorrect: Array.isArray(field.expectedValue) ? selectionsMatch(userValue, field.expectedValue) : null });
      return;
    }
    if (field && field.expectedValue != null) {
      results.push({
        field,
        userAnswer: String(userValue ?? ""),
        expectedAnswer: String(field.expectedValue),
        isCorrect: normalize(userValue) === normalize(field.expectedValue)
      });
    }
  });

  return results;
}

/**
 * Universal renderer for Form Fields.
 * Replaces the old table-based layout with a flexible div-based layout.
 * Applies attributes to Container, Label, and Input categories.
 */
export function createFormFieldDom(field: FormFieldDto, doc: Document = document, blankNumber?: number, layout?: FieldLayout): HTMLElement {
    // 1. Create Label
    const label = doc.createElement("label");
    label.innerHTML = markdownHtml(field.label, true);
    label.className = 'markdown-inline';
    label.setAttribute("for", field.key);

    // 2. Build Input based on Type (Existing logic preserved)
    let input: HTMLElement;

    switch (field.type) {
        case 'inline-text': {
            const text = doc.createElement('input');
            text.type = 'text';
            text.className = 'inline-text-input';
            text.setAttribute('aria-label', markdownText(field.label) || (blankNumber ? 'Blank ' + blankNumber : 'Blank'));
            input = text;
            break;
        }
        case "textarea": {
            const textarea = doc.createElement("textarea") as HTMLTextAreaElement;
            textarea.className = "p-inputtextarea p-inputtext p-component";
            input = textarea;
            break;
        }
        case 'checkbox-group':
        case 'multi-select': {
            const group = doc.createElement(field.type === 'multi-select' ? 'details' : 'fieldset');
            group.setAttribute('data-multi-choice', field.type);
            group.className = field.type === 'multi-select' ? 'multi-choice-dropdown' : 'multi-choice-group';
            group.setAttribute('aria-labelledby', `${field.key}-label`);
            label.id = `${field.key}-label`;
            label.removeAttribute('for');
            group.setAttribute('data-placeholder', field.placeholder || 'Select options...');
            const choices = doc.createElement('div');
            choices.className = field.type === 'multi-select' ? 'multi-choice-options' : '';
            const defaults = selectionKeys(field.defaultValue);
            const selectedLabels: string[] = [];
            (field.options ?? []).forEach(option => {
                const optionLabel = doc.createElement('label');
                const checkbox = doc.createElement('input');
                checkbox.type = 'checkbox';
                checkbox.name = field.key;
                checkbox.value = option.key;
                checkbox.checked = defaults.includes(option.key);
                if (checkbox.checked) {
                    checkbox.setAttribute('checked', '');
                    selectedLabels.push(option.value);
                }
                const optionText = doc.createElement('span');
                optionText.className = 'multi-choice-option-text';
                optionText.textContent = option.value;
                optionLabel.append(checkbox, optionText);
                choices.appendChild(optionLabel);
            });
            if (field.type === 'multi-select') {
                const summary = doc.createElement('summary');
                summary.textContent = selectedLabels.join(', ') || field.placeholder || 'Select options...';
                group.appendChild(summary);
            }
            group.appendChild(choices);
            input = group;
            break;
        }
        case "select": {
            const select = doc.createElement("select") as HTMLSelectElement;
            select.className = "p-dropdown p-component";

            const hasDefault = field.defaultValue !== null && field.defaultValue !== undefined && field.defaultValue !== "";

            const placeholderOption = doc.createElement("option");
            placeholderOption.value = "";
            placeholderOption.textContent = field.placeholder || "Select an option...";
            placeholderOption.selected = !hasDefault;
            placeholderOption.hidden = true;
            select.appendChild(placeholderOption);

            (field.options ?? []).forEach(opt => {
                const option = doc.createElement("option");
                option.value = opt.value;
                option.textContent = opt.value;
                if (hasDefault && String(field.defaultValue) === opt.value) option.selected = true;
                select.appendChild(option);
            });

            if (!hasDefault) {
                select.value = "";
                select.selectedIndex = 0;
            }
            input = select;
            break;
        }
        case "radio": {
            const container = doc.createElement("span");
            container.setAttribute('role', 'radiogroup');
            container.setAttribute('aria-labelledby', `${field.key}-label`);
            label.id = `${field.key}-label`;
            label.removeAttribute('for');
            container.id = field.key;
            container.setAttribute("name", field.key);
            container.setAttribute("data-field-key", field.key);

            (field.options ?? []).forEach((opt, index) => {
                if (index > 0 && !layout) container.appendChild(doc.createElement("br"));
                const radio = doc.createElement("input") as HTMLInputElement;
                radio.type = "radio";
                radio.name = field.key;
                radio.value = opt.value;
                radio.className = "p-radiobutton p-component";
                radio.setAttribute("data-field-key", field.key);

                const radioLabel = doc.createElement("label");
                radioLabel.appendChild(radio);
                radioLabel.appendChild(doc.createTextNode(opt.value));

                container.appendChild(radioLabel);
            });
            input = container;
            break;
        }
        case "checkbox": {
            const checkbox = doc.createElement("input") as HTMLInputElement;
            checkbox.type = "checkbox";
            checkbox.className = "p-checkbox p-component";
            input = checkbox;
            break;
        }
        default: {
            const inputEl = doc.createElement("input") as HTMLInputElement;
            inputEl.type = field.type === "text" ? "text" : field.type;
            inputEl.className = "p-inputtext p-component";
            input = inputEl;
            break;
        }
    }

    // 3. Set standard identifying attributes
    input.setAttribute("id", field.key);
    input.setAttribute("name", field.key);
    input.setAttribute("data-field-key", field.key);
    if (field.placeholder) input.setAttribute("placeholder", field.placeholder);
    if (field.required && !expectsBlankInline(field.type, field.expectedValue)) input.setAttribute("required", "true");
    if (field.maxLength) input.setAttribute("maxlength", String(field.maxLength));

    // 4. Helper to apply the Record<string, string>[] attributes
    const applyAttrs = (el: HTMLElement, attrList?: Record<string, string>[]) => {
        if (!attrList) return;
        attrList.forEach(record => {
            const entries = Object.entries(record);
            if (entries.length === 0) return;

            const [key, value] = entries[0];
            if (key) {
                if (key.toLowerCase() === 'style') {
                    // Append to style string to preserve flex/base defaults
                    el.style.cssText = `${el.style.cssText} ${value}`;
                } else {
                    el.setAttribute(key, value);
                }
            }
        });
    };

    // Set the default before author attributes so an explicit width can override it.
    if (field.type === 'text' || field.type === 'textarea') {
        input.style.width = '50%';
    }
    if (layout) {
        if (!['checkbox', 'radio', 'checkbox-group'].includes(field.type)) input.style.width = layout.inputWidth;
        if (field.type === 'textarea' && layout.textareaHeight) input.style.height = layout.textareaHeight;
        if (field.type === 'radio' || field.type === 'checkbox-group') {
            input.style.flexDirection = layout.orientation === 'horizontal' ? 'row' : 'column';
            const options = field.type === 'checkbox-group' ? input.firstElementChild as HTMLElement : input;
            if (options) {
                options.style.display = 'flex'; options.style.flexWrap = 'wrap'; options.style.gap = '0.5rem';
                options.style.flexDirection = options === input ? input.style.flexDirection : 'inherit';
            }
        }
    }

    // 5. Apply Categorized Attributes to Label and Input
    applyAttrs(label, field.attributes?.label);
    applyAttrs(input, field.attributes?.input);
    if (field.is_label_hidden) {
        label.classList.add('field-label-hidden');
        label.hidden = true;
        label.id = `${field.key}-label`;
        // Standalone inline fields omit the label and already have an aria-label.
        if (field.type !== 'inline-text' || layout) input.setAttribute('aria-labelledby', label.id);
    }

    // 6. Handle Default Values (Logic preserved)
    const val = String(field.defaultValue ?? '');
    if (!isMultiChoiceType(field.type) && val.length > 0) {
        if (field.type === "radio") {
            input.querySelectorAll("input[type=radio]").forEach(r => {
                const radio = r as HTMLInputElement;
                if (radio.value === val) radio.setAttribute("checked", "true");
            });
        } else if (field.type === "checkbox") {
            if (field.defaultValue === true) (input as HTMLInputElement).setAttribute("checked", "true");
        } else if (field.type === "select") {
            const select = input as HTMLSelectElement;
            Array.from(select.options).forEach(opt => { if (opt.value === val) opt.selected = true; });
        } else if (field.type === "textarea") {
            input.textContent = val;
        } else {
            (input as HTMLInputElement).setAttribute("value", val);
        }
    } else {
        if (field.type === "select") {
            (input as HTMLSelectElement).selectedIndex = 0;
        }
    }

    // 7. Assemble into Flex Container
    if (field.type === 'inline-text' && !layout) {
        const container = doc.createElement('span');
        container.className = 'inline-text-field-container';
        applyAttrs(container, field.attributes?.container);
        input.classList.add('inline-text-input');
        container.appendChild(input);
        addMarkdownFieldHelp(container, input, field, doc);
        return container;
    }
    const container = doc.createElement("div");
    container.className = "form-field-container";
    container.style.display = "flex";
    container.style.alignItems = "center";
    container.style.gap = "8px";

    // Apply Container-specific attributes
    applyAttrs(container, field.attributes?.container);
    if (field.is_label_hidden) container.classList.add('is-label-hidden');

    container.appendChild(label);
    container.appendChild(input);
    addMarkdownFieldHelp(container, input, field, doc);

    return container;
}

/**
 * Renders a bare dropdown for inline quiz targets.
 */
export function createQuizDropdownDom(
  field: FormFieldDto, 
  expectedKey: string | null, 
  doc: Document
): HTMLElement {
  const select = doc.createElement("select");
  // Reuse your existing p-dropdown class for consistent styling
  select.className = "p-dropdown p-component quiz-target-input";
  
  select.setAttribute("data-field-key", field.key);
  if (expectedKey) {
    select.setAttribute("data-expected-key", expectedKey);
  }

  // Add placeholder
  const placeholderOption = doc.createElement("option");
  placeholderOption.value = "";
  placeholderOption.textContent = field.placeholder || "Select...";
  placeholderOption.selected = true;
  select.appendChild(placeholderOption);

  // Add options from the quiz-list field
  (field.options ?? []).forEach(opt => {
    const option = doc.createElement("option");
    // opt is an object { key, value } in your quiz-list
    option.value = opt.key; 
    option.textContent = opt.value;
    select.appendChild(option);
  });

  return select;
}
